import {
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogActions,
  Theme,
  CircularProgress,
} from "@mui/material";
import React, { FC, ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  isPending: boolean;
  theme: Theme;
  title: string;
  content: ReactNode;
  actions?: ReactNode;
  size?: {
    width: string | number;
    minHeight: number;
    maxHeight: number;
    maxWidth: number;
  };
  dataTestId?: string;
  closeButtonTestId?: string;
};

const Modal: FC<ModalProps> = ({
  open,
  onClose,
  isPending,
  theme,
  title,
  content,
  actions,
  size = {
    width: "100%",
    minHeight: 640,
    maxHeight: 720,
    maxWidth: 760,
  },
  dataTestId,
  closeButtonTestId,
}) => {
  return (
    <Dialog
      onClose={() => {
        if (isPending) {
          return;
        }

        onClose();
      }}
      aria-labelledby="dialog-container"
      sx={{
        "& .MuiDialog-paper": {
          // 10px, not MUI's default 4px. Set on the SHARED component on purpose: every modal in
          // the back office renders through here, and a single rounded corner radius is the point
          // — one modal at 10px beside twenty-five at 4px is the inconsistency, not the fix.
          borderRadius: "10px",
          ...size,
          margin: {
            xs: "0.5rem",
            sm: "0.75rem",
          },
          // Never taller than the viewport: a fixed maxHeight above the window height pushes the
          // footer actions (Save) off-screen with no way to reach them. The content scrolls instead.
          maxHeight: {
            xs: "100%",
            sm: `min(${size.maxHeight}px, calc(100% - 64px))`,
          },
        },
      }}
      open={open}
      disableEscapeKeyDown={isPending}
      data-testid={dataTestId}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "60px",
        }}
        id="dialog-title"
        variant="headlineSmall"
        color={theme.palette.surface?.light || "#FFFFFF"}
        bgcolor={theme.palette.primary.dark || "#000000"}
      >
        {title}

        <IconButton
          aria-label="close"
          data-testid={closeButtonTestId}
          onClick={() => {
            if (isPending) {
              return;
            }

            onClose();
          }}
          sx={{
            color: theme.palette.surface?.light || "#FFFFFF",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          gap: "1.25rem",
          display: "flex",
          flexDirection: "column",
          padding: {
            xs: "0.25rem",
            sm: "1.25rem 1.25rem 0.75rem 1.25rem",
          },
        }}
      >
        {content}
        {isPending && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)", // Semi-transparent overlay
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </div>
        )}
      </DialogContent>
      {actions && (
        <DialogActions
          sx={{
            borderTop: "1px solid #ccc",
            p: "20px",
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;
